import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth, PLP_LOGO, ROBYN_PHOTO, COMMUNITY_PHOTOS } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Settings, Image, Upload, Link, Save, RefreshCw } from 'lucide-react';

const AdminSettings = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('images');
  
  // Current images
  const currentImages = {
    logo: PLP_LOGO,
    robyn: ROBYN_PHOTO,
    community_main: COMMUNITY_PHOTOS.main,
    community_group: COMMUNITY_PHOTOS.group,
    community_family: COMMUNITY_PHOTOS.family,
    community_gathering: COMMUNITY_PHOTOS.gathering
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Settings className="w-6 h-6 text-slate-600" /> Site Settings
        </h1>
        <p className="text-slate-600">Manage site-wide settings and media</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border">
          <TabsTrigger value="images"><Image className="w-4 h-4 mr-2" /> Images</TabsTrigger>
          <TabsTrigger value="content"><Link className="w-4 h-4 mr-2" /> Quick Links</TabsTrigger>
        </TabsList>

        <TabsContent value="images" className="mt-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Logo */}
            <Card className="bg-white">
              <CardHeader><CardTitle className="text-lg">PLP Logo</CardTitle></CardHeader>
              <CardContent>
                <img src={currentImages.logo} alt="PLP Logo" className="w-full h-32 object-contain bg-slate-50 rounded-lg mb-4" />
                <p className="text-xs text-slate-500 break-all">{currentImages.logo}</p>
              </CardContent>
            </Card>

            {/* Robyn Photo */}
            <Card className="bg-white">
              <CardHeader><CardTitle className="text-lg">Robyn Lyons Photo</CardTitle></CardHeader>
              <CardContent>
                <img src={currentImages.robyn} alt="Robyn Lyons" className="w-full h-32 object-cover rounded-lg mb-4" />
                <p className="text-xs text-slate-500 break-all">{currentImages.robyn}</p>
              </CardContent>
            </Card>

            {/* Community Main */}
            <Card className="bg-white">
              <CardHeader><CardTitle className="text-lg">Community Main</CardTitle></CardHeader>
              <CardContent>
                <img src={currentImages.community_main} alt="Community" className="w-full h-32 object-cover rounded-lg mb-4" />
                <p className="text-xs text-slate-500">Used on: Homepage, News</p>
              </CardContent>
            </Card>

            {/* Community Group */}
            <Card className="bg-white">
              <CardHeader><CardTitle className="text-lg">Community Group</CardTitle></CardHeader>
              <CardContent>
                <img src={currentImages.community_group} alt="Community Group" className="w-full h-32 object-cover rounded-lg mb-4" />
                <p className="text-xs text-slate-500">Used on: Get Involved</p>
              </CardContent>
            </Card>

            {/* Community Family */}
            <Card className="bg-white">
              <CardHeader><CardTitle className="text-lg">Community Family</CardTitle></CardHeader>
              <CardContent>
                <img src={currentImages.community_family} alt="Community Family" className="w-full h-32 object-cover rounded-lg mb-4" />
                <p className="text-xs text-slate-500">Used on: Spotlights</p>
              </CardContent>
            </Card>

            {/* Community Gathering */}
            <Card className="bg-white">
              <CardHeader><CardTitle className="text-lg">Community Gathering</CardTitle></CardHeader>
              <CardContent>
                <img src={currentImages.community_gathering} alt="Community Gathering" className="w-full h-32 object-cover rounded-lg mb-4" />
                <p className="text-xs text-slate-500">Used on: Resources</p>
              </CardContent>
            </Card>
          </div>

          <Card className="mt-6 bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <p className="text-amber-800 text-sm">
                <strong>Note:</strong> To update site images, upload new images and update the URLs in the codebase. 
                Contact your developer to make changes to the main site images.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="content" className="mt-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-white">
              <CardHeader><CardTitle className="text-lg">Social Media Links</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Facebook Page</Label>
                  <Input placeholder="https://facebook.com/..." />
                </div>
                <div>
                  <Label>Instagram</Label>
                  <Input placeholder="https://instagram.com/..." />
                </div>
                <div>
                  <Label>YouTube Channel</Label>
                  <Input placeholder="https://youtube.com/..." />
                </div>
                <Button className="w-full bg-plp-blue"><Save className="w-4 h-4 mr-2" /> Save Links</Button>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardHeader><CardTitle className="text-lg">Donation Settings</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>SunCash URL</Label>
                  <Input defaultValue="https://mysuncash.com" />
                </div>
                <div>
                  <Label>Donation Message</Label>
                  <Input defaultValue="Your contribution helps us reach more residents" />
                </div>
                <Button className="w-full bg-plp-blue"><Save className="w-4 h-4 mr-2" /> Save Settings</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSettings;
